/**
 * Os recortes de data que as listas oferecem com um clique — F132.
 *
 * "O que vendi hoje", "o que vence nesta semana": perguntas de balcão que as
 * listas só respondiam com o total de sempre. Fica no fuso do navegador, que é
 * o da loja: "hoje" é o dia do relógio da parede, e não o dia em UTC.
 *
 * Devolve ISO completos, porque a API compara instantes; o fim é o último
 * milissegundo do dia, e não a meia-noite seguinte, para um título que vence
 * amanhã à 00:00 não entrar em "hoje".
 */
export type PeriodKind =
  'today' | 'yesterday' | 'last7' | 'last30' | 'next7' | 'month' | 'lastMonth'

/**
 * O que um seletor de período oferece: os atalhos mais "escolher datas" — F158.
 *
 * Mora aqui, e não em `components/period-picker.tsx`, pela mesma razão que
 * `lib/config-tabs.ts` existe: um módulo que mistura componente com constantes
 * faz o Fast Refresh desistir do arquivo inteiro, em silêncio.
 */
export type PeriodChoice = PeriodKind | 'custom'

export const PERIOD_CHOICES: readonly PeriodChoice[] = [
  'today',
  'yesterday',
  'last7',
  'last30',
  'month',
  'lastMonth',
  'custom',
]

export const isPeriodChoice = (value: unknown): value is PeriodChoice =>
  typeof value === 'string' &&
  (PERIOD_CHOICES as readonly string[]).includes(value)

export const PERIOD_LABELS: Record<PeriodKind, string> = {
  today: 'Hoje',
  yesterday: 'Ontem',
  last7: 'Últimos 7 dias',
  // F161: o app que o dono usa no celular tem "30 dias" ao lado de "7 dias".
  last30: 'Últimos 30 dias',
  next7: 'Próximos 7 dias',
  month: 'Este mês',
  lastMonth: 'Mês passado',
}

const startOfDay = (at: Date): Date =>
  new Date(at.getFullYear(), at.getMonth(), at.getDate(), 0, 0, 0, 0)

const endOfDay = (at: Date): Date =>
  new Date(at.getFullYear(), at.getMonth(), at.getDate(), 23, 59, 59, 999)

export const periodRange = (
  kind: PeriodKind,
  now: Date = new Date()
): { from: string; to: string } => {
  switch (kind) {
    case 'today':
      return {
        from: startOfDay(now).toISOString(),
        to: endOfDay(now).toISOString(),
      }
    case 'last7': {
      const first = new Date(now)
      first.setDate(first.getDate() - 6)
      return {
        from: startOfDay(first).toISOString(),
        to: endOfDay(now).toISOString(),
      }
    }
    case 'last30': {
      const first = new Date(now)
      first.setDate(first.getDate() - 29)
      return {
        from: startOfDay(first).toISOString(),
        to: endOfDay(now).toISOString(),
      }
    }
    case 'next7': {
      const last = new Date(now)
      last.setDate(last.getDate() + 6)
      return {
        from: startOfDay(now).toISOString(),
        to: endOfDay(last).toISOString(),
      }
    }
    case 'yesterday': {
      const before = new Date(now)
      before.setDate(before.getDate() - 1)
      return {
        from: startOfDay(before).toISOString(),
        to: endOfDay(before).toISOString(),
      }
    }
    case 'month':
      return {
        from: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(),
        to: endOfDay(
          new Date(now.getFullYear(), now.getMonth() + 1, 0)
        ).toISOString(),
      }
    case 'lastMonth':
      return {
        from: new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString(),
        to: endOfDay(
          new Date(now.getFullYear(), now.getMonth(), 0)
        ).toISOString(),
      }
  }
}

/**
 * Um período digitado nos dois campos de data (F134): `2026-09-01` a
 * `2026-09-13`, os dois dias inteiros, no fuso do navegador. Nulo quando falta
 * um dos dois ou quando o fim vem antes do começo.
 */
export const customRange = (
  fromDay: string,
  toDay: string
): { from: string; to: string } | null => {
  const parse = (value: string): Date | null => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
    if (match === null) return null
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  }
  const first = parse(fromDay)
  const last = parse(toDay)
  if (first === null || last === null || last < first) return null
  return {
    from: startOfDay(first).toISOString(),
    to: endOfDay(last).toISOString(),
  }
}

/**
 * O fim EXCLUSIVO que a API do painel pede: o milissegundo seguinte ao último
 * instante do período. As listas usam o fim inclusivo; o painel compara
 * `issued_at < to`, e uma venda às 23:59:59,9995 não pode cair entre os dois.
 */
export const exclusiveEnd = (inclusiveTo: string): string =>
  new Date(new Date(inclusiveTo).getTime() + 1).toISOString()
